import { Box, Button, Tooltip, Typography } from "@mui/material";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { gstReturnCategoryMenu, regularRegistrationTaskMenu } from "data/constants";
import useQueryParams from "hooks/useQueryParams";
import { useNavigate } from "react-router-dom";
import useTitle from "hooks/useTitle";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Permissions } from "data/permissons";
import { usePermissions } from "context/PermissionsProvider";
import AccessDenied from "views/atom-pro/Access/accessDenide";
import IffTask from "views/register/GstReturns/Regular/ReturnTypes/Iff/taskIff";
import Gstr1aTask from "views/register/GstReturns/Regular/ReturnTypes/Gstr-1A/gstr1aTask";
import Gstr3bTask from "views/register/GstReturns/Regular/ReturnTypes/Gstr-3b/gstr3bTask";
import Gstr2xTask from "views/register/GstReturns/Regular/ReturnTypes/Gstr-2x/gstr2xTask";
import Gstr9Task from "views/register/GstReturns/Regular/ReturnTypes/Gstr-9/gstr9Task";
import Gstr9cTask from "views/register/GstReturns/Regular/ReturnTypes/Gstr-9/gstr9cTask";
import Gstr4Task from "views/register/GstReturns/Regular/ReturnTypes/Gstr-9/gstr4Task";
import Cmp08Task from "views/register/GstReturns/Regular/ReturnTypes/CMP-08/cmp08Task";
import Gstr6Task from "views/register/GstReturns/Distributor/gstr6Task";
import Gstr7Task from "views/register/GstReturns/Deductor/gstr7Task";
import Gstr8Task from "views/register/GstReturns/Collector/gstr8Task";
import AddGstRecord from "views/register/GstReturns/AddGstRecord";
import { SelectionContext, SelectionInfo, SelectionSetterContext } from "views/register/GstReturns/design/ToolbarSlot";
import { SegmentedTab, SegmentedTabs } from "views/register/GstReturns/design/SegmentedTabs";
import { useState } from "react";

const REGULAR_TAXPAYER = "Regular Taxpayer";

function GstrTaskIndex() {
  useTitle("Sync GST & Tasks");
  const { permissions } = usePermissions();
  const navigate = useNavigate();
  const { queryParams } = useQueryParams();
  const category = queryParams.category || REGULAR_TAXPAYER;
  const active = queryParams.tab || "GSTR-1/IFF";
  // Shared across all tabs - AddGstRecord is a generic client picker, not
  // tied to whichever return type tab is active, so one instance here
  // (opened from the tab row's own "+ Add" button) replaces what used to be
  // a FloatingButton + modal duplicated per page.
  const [addOpen, setAddOpen] = useState(false);
  // The active tab's live selection (count + remove handler), published via
  // useSelectionSync so the "⊖ Remove" button here can reflect whichever
  // page is currently showing, without each page needing to be aware of
  // this shell directly - same mechanism the GST Return Tracker uses.
  const [selection, setSelection] = useState<SelectionInfo>({ count: 0 });

  if (!(permissions.includes(Permissions.VIEW_ALL_CLIENT_MANAGERS) || permissions.includes(Permissions.VIEW_ASSIGNED_CLIENT_MANAGERS))) {
    return <AccessDenied />
  }

  return (
    <SelectionContext.Provider value={selection}>
    <SelectionSetterContext.Provider value={setSelection}>
      <Box sx={{ position: "sticky", top: "var(--atom-appbar-height, 64px)", bgcolor: "#FFF", zIndex: 2 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: 1.5, px: 2, py: 1.6 }}>
          <Box display="flex" alignItems="center" gap={1.5} sx={{ minWidth: 0, overflow: "hidden" }}>
            <Box sx={{ width: 32, height: 32, borderRadius: "9px", bgcolor: "#e8f0ff", color: "#2563eb", display: "grid", placeItems: "center", flexShrink: 0 }}>
              <ReceiptLongOutlinedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontSize: 18, fontWeight: 600, flexShrink: 0, whiteSpace: "nowrap" }}>GST Return Filing Tracker</Typography>
          </Box>
          <Box sx={{ overflowX: "auto" }}>
            <SegmentedTabs>
              {gstReturnCategoryMenu.map((item, index) => (
                <SegmentedTab key={index} onClick={() => navigate(`?category=${item.title}`)} active={category === item.title}>
                  {item.title}
                </SegmentedTab>
              ))}
            </SegmentedTabs>
          </Box>
          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button
              size="small" variant="outlined" color="error" onClick={selection.onRemove} disabled={!selection.count || selection.removing}
              sx={{ textTransform: "none", fontSize: 12 }}
            >
              {selection.removing ? "Removing…" : "⊖ Remove"}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => setAddOpen(true)}
              sx={{ textTransform: "none", boxShadow: "none" }}
            >
              + Add
            </Button>
          </Box>
        </Box>
        {category === REGULAR_TAXPAYER && (
          <Box sx={{ overflowX: "auto", display: "flex", justifyContent: "center" }}>
            <SegmentedTabs>
              {regularRegistrationTaskMenu?.map((item, index) => (
                <SegmentedTab
                  key={index}
                  onClick={() => navigate(`?category=${REGULAR_TAXPAYER}&tab=${item.title}`)}
                  active={active === item.title}
                >
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {item.title}
                    {item?.tool && (
                      <Tooltip title={item.tool}>
                        <HelpOutlineIcon fontSize="small" sx={{ fontSize: 14 }} />
                      </Tooltip>
                    )}
                  </Box>
                </SegmentedTab>
              ))}
            </SegmentedTabs>
          </Box>
        )}
      </Box>
      <AddGstRecord open={addOpen} setOpen={setAddOpen} title="Add Record" />
      {category === REGULAR_TAXPAYER && (
        <Box px={2} py={3} pb={0} mb={0}>
          {active === "GSTR-1/IFF" && <IffTask />}
          {active === "GSTR-1A" && <Gstr1aTask />}
          {active === "GSTR-3B" && <Gstr3bTask />}
          {active === "GSTR-2X" && <Gstr2xTask />}
          {active === "GSTR-9" && <Gstr9Task />}
          {active === "GSTR-9C" && <Gstr9cTask />}
          {active === "CMP-08" && <Cmp08Task />}
          {active === "GSTR-4" && <Gstr4Task />}
        </Box>
      )}
      {category === "Input Service Distributor (GSTR-6)" && (
        <Box px={2} py={3} pb={0} mb={0}>
          <Gstr6Task />
        </Box>
      )}
      {category === "Tax Deductor (GSTR-7)" && (
        <Box px={2} py={3} pb={0} mb={0}>
          <Gstr7Task />
        </Box>
      )}
      {category === "Tax Collector (GSTR-8)" && (
        <Box px={2} py={3} pb={0} mb={0}>
          <Gstr8Task />
        </Box>
      )}
    </SelectionSetterContext.Provider>
    </SelectionContext.Provider>
  );
}

export default GstrTaskIndex;
